<template>
  <Card class="card">
    <View direction="column" gap="md" v-if="pageLoaded">
      <View direction="column" gap="sm">
        <View justify="space-between" direction="row">
          <Text variant="h2">{{ renter?.user.name }}'s application</Text>
          <ApplicationBundleStatusBadge
            :status="bundle?.status!"
            v-if="bundle?.status === ApplicationBundleStatus.SUBMITTED"
          />
        </View>

        <Text class="applied-date" variant="body-small" v-if="bundle?.submittedAt">
          Submitted {{ format(new Date(bundle.submittedAt), 'MMM, d') }}
        </Text>
      </View>
      <Text :variant="'body-large'">{{ summaryString }}</Text>

      <RenterProfileApplication
        v-for="application in renterProfileStore.applications.selected"
        :application="application"
      />
    </View>
    <View class="loading-wrap" direction="column" justify="center" align="center" v-else>
      <LoadingIndicator />
    </View>
  </Card>
</template>

<script setup lang="ts">
import { format } from 'date-fns';
import pluralize from 'pluralize';
import { ref, onMounted, computed } from 'vue';
import { useRoute } from 'vue-router';

import { ApplicantType, ApplicationBundleStatus } from '@tallo/applications';
import { Card, LoadingIndicator, Text, View } from '@tallo/design-system';
import { ApplicationBundleStatusBadge } from '@tallo/investor/applications';

import { useRenterProfileStore } from '../store/renter-profile.store';
import RenterProfileApplication from './renter-profile-application.vue';

const route = useRoute();
const renterId = ref(route.params.renterId as string);
const bundleId = ref(route.params.applicationId as string);
const renterProfileStore = useRenterProfileStore();
const renter = computed(() => renterProfileStore.getRenterProfile);
const bundle = computed(() => renterProfileStore.bundles.bundles.find((bundle) => bundle.id === bundleId.value));
const pageLoaded = computed(
  () =>
    renterProfileStore.bundles.isLoaded &&
    renterProfileStore.applications.isLoaded &&
    renterProfileStore.profile.isLoaded,
);

const adultCount = computed(() => {
  return renterProfileStore.applications.selected.filter(
    (application) => application.type === ApplicantType.APPLICANT || application.type === ApplicantType.CO_APPLICANT,
  ).length;
});
const childrenCount = computed(() => bundle.value?.numberOfOccupantsUnderEighteen ?? 0);
const petsCount = computed(() => {
  return renterProfileStore.applications.selected.reduce((acc, application) => {
    const renterDetails = application.renterDetails;
    return (
      acc +
      (renterDetails?.numberOfSmallDogs ?? 0) +
      (renterDetails?.numberOfMediumDogs ?? 0) +
      (renterDetails?.numberOfLargeDogs ?? 0) +
      (renterDetails?.numberOfCats ?? 0) +
      (renterDetails?.numberOfOtherPets ?? 0)
    );
  }, 0);
});

const summaryString = computed(() => {
  const adults = pluralize('adult', adultCount.value, true);
  const minors = childrenCount.value > 0 ? ` and ${pluralize('minor', childrenCount.value, true)}` : '';
  const pets = pluralize('pet', petsCount.value, true);
  return `${adults}${minors} ∙ ${pets}`;
});

onMounted(() => {
  renterProfileStore.loadProfileById(renterId.value);
  renterProfileStore.loadBundles(renterId.value);
  renterProfileStore.loadApplicationsByBundleId(bundleId.value);
});
</script>

<style scoped lang="scss">
.card {
  width: 100%;
}

.applied-date {
  color: var(--t-color-neutral-700);
}

.loading-wrap {
  height: 100%;
}
</style>
